import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("America/New_York");

export const formatTimeToHHMM12Hr = (time) => {
  if (!time || time === "00:00:00") return "N/A";
  const parsed = dayjs.tz(`1970-01-01T${time}`);
  if (!parsed.isValid()) return "N/A";
  return parsed.format("h:mm A");
};

export const formatDateLong = (date) => {
  if (!date) return "N/A";
  const parsed = dayjs.tz(date);
  if (!parsed.isValid()) return "N/A";
  return parsed.format("dddd, MMMM D, YYYY");
};

export const formatDateLongNoYear = (date) => {
  if (!date) return "N/A";
  const parsed = dayjs.tz(date);
  if (!parsed.isValid()) return "N/A";
  return parsed.format("dddd, MMMM D");
};

export const formatDateLongWithTime = (date, timezone, ampm = true) => {
  if (!date) return "N/A";
  const parsed = timezone ? dayjs.tz(date, timezone) : dayjs.tz(date);
  if (!parsed.isValid()) return "N/A";
  const formatString = ampm
    ? "dddd, MMMM D, YYYY h:mm A"
    : "dddd, MMMM D, YYYY HH:mm";
  return parsed.format(formatString);
};

export function formatSelectedMonths(months = []) {
  const list = Array.isArray(months) ? months.filter(Boolean) : [];
  const len = list.length;
  if (len === 0) return "";
  if (len === 12) return "all months of";
  if (len === 1) return list[0];
  if (len === 2) return `${list[0]}, and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list[len - 1]}`;
}

export const formatDate = (dateString) => {
  try {
    if (dateString === null) return "Date is Null";
    const parsedDate = dayjs.tz(dateString);

    if (!parsedDate.isValid()) {
      throw new Error("Invalid date");
    }

    return parsedDate.format("MM-DD-YYYY");
  } catch (error) {
    console.error("Date parsing error:", error);
    return "Invalid Date";
  }
};

export const formatDurationToHHMM = (duration) => {
  if (!duration) return "N/A";
  return duration.split(":").slice(0, 2).join(":");
};

export const formatAmount = (amount, digits = 2) => {
  if (
    !amount ||
    amount === 0 ||
    amount === "0.00" ||
    parseFloat(amount) === 0
  ) {
    return "N/A";
  }
  const numAmount = parseFloat(amount);
  // Check if the number is a whole number
  if (numAmount % 1 === 0) {
    return `$${numAmount.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  }
  return `$${numAmount.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
};

export const formatMileage = (mileage) => {
  if (
    !mileage ||
    mileage === 0 ||
    mileage === "0.00" ||
    parseFloat(mileage) === 0
  ) {
    return "N/A";
  }
  return parseFloat(mileage).toFixed(2);
};

export const formatDateTime = (date) => {
  const parsed = dayjs.tz(date);
  if (!parsed.isValid()) return "Invalid Date";
  return parsed.format("YYYY-MM-DD HH:mm:ss");
};

export const getStatusColor = (theme, status) => {
  switch (status) {
    case "Completed":
      return theme.palette.primary.main;
    case "Delivered":
    case "Confirmed":
      return theme.palette.info[400];
    case "In-Progress":
    case "Requested":
      return theme.palette.error[400];
    default:
      return theme.palette.grey[400];
  }
};

export const getProjectNumber = (date, listOfServices) => {
  try {
    const baseDate = date ? dayjs.tz(date) : dayjs.tz();
    const theDate = baseDate.format("YYYY-MM-DD");

    const index =
      listOfServices.filter((service) => {
        return dayjs.tz(service.Date).format("YYYY-MM-DD") === theDate;
      }).length + 1;

    const paddedIndex = index < 10 ? `0${index}` : `${index}`;

    const projectNumber = baseDate.format("YYMMDD") + paddedIndex;

    return projectNumber;
  } catch (e) {
    console.log("ERROR: ", e);
    return date;
  }
};

// Returns a color based on how soon a given date is relative to today
// Cases: today, tomorrow, this week, next week, else
// If status is "Completed" or "Cancelled", returns normal color regardless of date
export const getDateProximityColor = (theme, dateInput, status) => {
  const colorNormal = theme.palette.primary.text;
  const colorToday = theme.palette.error[400];
  const colorTomorrow = theme.palette.warning[400];
  const colorThisWeek = theme.palette.info[400];
  const colorNextWeek = theme.palette.warning[300]; // Changed from primary.text so it's visible

  try {
    // Don't apply proximity coloring for completed or cancelled assignments
    if (status === "Completed" || status?.startsWith("Cancelled")) {
      return colorNormal;
    }

    if (!dateInput) {
      return colorNormal; // default/else color
    }

    let source = null;
    const direct = dayjs.tz(dateInput);
    if (direct.isValid()) {
      source = direct;
    } else if (typeof dateInput === "string") {
      const d = dayjs.tz(dateInput);
      if (d.isValid()) source = d;
    } else if (dateInput instanceof Date) {
      source = dayjs.tz(dateInput);
    }

    if (!source || !source.isValid()) {
      return colorNormal;
    }
    const target = source.startOf("day");

    const today = dayjs().startOf("day");

    // Today
    if (target.isSame(today, "day")) return colorToday;

    // Tomorrow
    if (target.isSame(today.add(1, "day"), "day")) return colorTomorrow;

    // This week (Monday to Sunday)
    const dayOfWeek = today.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysUntilMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Calculate offset to Monday
    const thisWeekMonday = today.add(daysUntilMonday, "day");
    const thisWeekSunday = thisWeekMonday.add(6, "day");

    if (
      target.isAfter(today, "day") &&
      (target.isSame(thisWeekMonday, "day") ||
        target.isAfter(thisWeekMonday, "day")) &&
      (target.isSame(thisWeekSunday, "day") ||
        target.isBefore(thisWeekSunday, "day"))
    ) {
      return colorThisWeek;
    }

    // Next week (Next Monday to Next Sunday)
    const nextWeekMonday = thisWeekMonday.add(7, "day");
    const nextWeekSunday = nextWeekMonday.add(6, "day");

    if (
      (target.isSame(nextWeekMonday, "day") ||
        target.isAfter(nextWeekMonday, "day")) &&
      (target.isSame(nextWeekSunday, "day") ||
        target.isBefore(nextWeekSunday, "day"))
    ) {
      return colorNextWeek;
    }

    // Else (past or beyond next week)
    return colorNormal;
  } catch (e) {
    return colorNormal;
  }
};

export const numericComparator = (v1, v2) => {
  if (v1 === "N/A") return 1;
  if (v2 === "N/A") return -1;
  // Remove currency symbols, commas, percent signs, and parse as float
  const n1 = parseFloat(String(v1).replace(/[$,% ]/g, ""));
  const n2 = parseFloat(String(v2).replace(/[$,% ]/g, ""));
  if (isNaN(n1)) return 1;
  if (isNaN(n2)) return -1;
  return n1 - n2;
};
// Dedicated helper for deadline-specific coloring logic.
// Only applies deadline proximity colors when status is "In-Progress".
// For all other statuses, returns primary.text.
export const getDeadlineProximityColor = (theme, dateInput, status) => {
  const deadlineColorNormal = theme.palette.primary.text;
  const deadlineColorToday =
    theme.palette.error[500] || theme.palette.error[400];
  const deadlineColorTomorrow =
    theme.palette.warning[500] || theme.palette.warning[400];
  const deadlineColorThisWeek =
    theme.palette.info[500] || theme.palette.info[400];
  const deadlineColorNextWeek = theme.palette.primary.text;
  const deadlineColorPast =
    theme.palette.error[700] ||
    theme.palette.error[600] ||
    theme.palette.error[500];

  try {
    // Only apply deadline proximity colors if status is "In-Progress"
    if (status !== "In-Progress") {
      return deadlineColorNormal;
    }

    if (!dateInput) return deadlineColorNormal;

    let source = null;
    const direct = dayjs.tz(dateInput);
    if (direct.isValid()) {
      source = direct;
    } else if (typeof dateInput === "string") {
      const d = dayjs.tz(dateInput);
      if (d.isValid()) source = d;
    } else if (dateInput instanceof Date) {
      source = dayjs.tz(dateInput);
    }

    if (!source || !source.isValid()) return deadlineColorNormal;
    const target = source.startOf("day");

    const today = dayjs().startOf("day");

    // Past (overdue)
    if (target.isBefore(today, "day")) return deadlineColorPast;

    // Today
    if (target.isSame(today, "day")) return deadlineColorToday;

    // Tomorrow
    if (target.isSame(today.add(1, "day"), "day")) return deadlineColorTomorrow;

    // This week (Monday to Sunday)
    const dayOfWeek = today.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysUntilMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Calculate offset to Monday
    const thisWeekMonday = today.add(daysUntilMonday, "day");
    const thisWeekSunday = thisWeekMonday.add(6, "day");

    if (
      target.isAfter(today, "day") &&
      (target.isSame(thisWeekMonday, "day") ||
        target.isAfter(thisWeekMonday, "day")) &&
      (target.isSame(thisWeekSunday, "day") ||
        target.isBefore(thisWeekSunday, "day"))
    ) {
      return deadlineColorThisWeek;
    }

    // Next week (Next Monday to Next Sunday)
    const nextWeekMonday = thisWeekMonday.add(7, "day");
    const nextWeekSunday = nextWeekMonday.add(6, "day");

    if (
      (target.isSame(nextWeekMonday, "day") ||
        target.isAfter(nextWeekMonday, "day")) &&
      (target.isSame(nextWeekSunday, "day") ||
        target.isBefore(nextWeekSunday, "day"))
    ) {
      return deadlineColorNextWeek;
    }

    // Else (beyond next week)
    return deadlineColorNormal;
  } catch (e) {
    return deadlineColorNormal;
  }
};

export const mergeJsonArrays = (
  objectFrom,
  objectTo,
  suffixFrom = "_FROM",
  suffixTo = "_TO",
) => {
  const result = {};

  // Iterate over the keys of the first object
  for (const key in objectFrom) {
    if (Object.prototype.hasOwnProperty.call(objectFrom, key)) {
      // Check if the value is an object (and not null or an array) to recursively merge them
      if (
        typeof objectFrom[key] === "object" &&
        objectFrom[key] !== null &&
        !Array.isArray(objectFrom[key]) &&
        typeof objectTo[key] === "object" &&
        objectTo[key] !== null &&
        !Array.isArray(objectTo[key])
      ) {
        result[key] = mergeJsonArrays(
          objectFrom[key],
          objectTo[key],
          suffixFrom,
          suffixTo,
        );
      } else {
        // Add keys from both objects with respective suffixes.
        result[key + suffixFrom] = objectFrom[key];
        result[key + suffixTo] = objectTo[key];
      }
    }
  }

  return result;
};

export function normalizePhoneNumber(phoneNumber) {
  if (
    !phoneNumber ||
    phoneNumber.trim() === "" ||
    phoneNumber.trim() === "N/A"
  ) {
    return "N/A";
  }

  // Split phone numbers by "|"
  const phoneNumbers = phoneNumber.split("|").map((num) => num.trim());

  const normalizedNumbers = phoneNumbers.map((num) => {
    // Determine if the number is international or local
    const isInternational = num.startsWith("+");

    // Regex for international numbers
    const internationalRegex =
      /^\+(\d{1,3})[-.\s]?(\d{1,4})[-.\s]?(\d{1,4})[-.\s]?(\d{1,4})(?:\s*x(\d+))?$/;

    // Regex for local USA numbers
    const localRegex =
      /^(\d{3})[-.\s]?(\d{3})[-.\s]?(\d{4})(?:\s*(?:ext\.?|x)\s*(\d+))?$/i;

    const match = isInternational
      ? num.match(internationalRegex)
      : num.match(localRegex);

    if (!match) {
      return "BAD: " + phoneNumber;
    }

    // Destructure the match array based on whether it's international or local
    let countryCode, areaCode, centralOfficeCode, lineNumber, extension;
    if (isInternational) {
      [, countryCode, areaCode, centralOfficeCode, lineNumber, extension] =
        match;
    } else {
      [, areaCode, centralOfficeCode, lineNumber, extension] = match;
    }

    // Format the phone number
    let formattedNumber = `(${areaCode}) ${centralOfficeCode}-${lineNumber}`;

    // Add country code if present
    if (isInternational && countryCode) {
      formattedNumber = `+${countryCode} ${formattedNumber}`;
    }

    // Add extension if present
    if (extension) {
      formattedNumber += ` x${extension}`;
    }

    return formattedNumber;
  });

  // Join normalized numbers with " | "
  return normalizedNumbers.join(" | ");
}
